import {
  Avatar,
  CircularProgress,
  DialogContent,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { client } from '../../../../async/utils/client';
import {
  GetGoogleCalendarEventsQuery,
  GetGoogleCalendarEventsQueryVariables,
  GoogleCalendar,
  GoogleCalendarEvent,
  useGetGoogleCalendarsQuery,
} from '../../../../graphql/gen/types';
import { GET_GOOGLE_CALENDAR_EVENTS } from '../../../../graphql/queries/GetGoogleCalendarEvents';

type EventSelectorProps = {
  selectedEvent: GoogleCalendarEvent | null;
  setSelectedEvent: (event: GoogleCalendarEvent | null) => void;
  setEventQuery: (value: string) => void;
  eventQuery: string;
  selectedCalendar: GoogleCalendar;
};

type EventState = {
  loading: boolean;
  events: Array<GoogleCalendarEvent>;
};

function useEvents(selectedCalendar: GoogleCalendar): EventState {
  const [eventState, setEventState] = useState<{ loading: boolean; events: Array<GoogleCalendarEvent> }>({
    loading: true,
    events: [],
  });

  useEffect(() => {
    const startTimeMin = moment().subtract(4, 'weeks').toDate();
    const startTimeMax = moment().add(4, 'weeks').toDate();

    // Go through all events reecursively till there there is no nextCursor
    async function queryAllEvents(
      cursor?: string,
      events: Array<GoogleCalendarEvent> = []
    ): Promise<Array<GoogleCalendarEvent>> {
      const res = await client.query<GetGoogleCalendarEventsQuery, GetGoogleCalendarEventsQueryVariables>({
        query: GET_GOOGLE_CALENDAR_EVENTS,
        variables: {
          calendarId: selectedCalendar.id,
          limit: 100,
          startTimeMin,
          startTimeMax,
          cursor,
        },
      });
      res.data?.currentUser?.googleCalendarEvents?.events?.forEach((ev) => events.push({ ...ev }));
      const nextCursor = res.data?.currentUser?.googleCalendarEvents?.nextCursor;
      if (nextCursor) {
        return queryAllEvents(nextCursor, events);
      } else {
        events.sort((ev1, ev2) => {
          const d1 = ev1.startTime ? new Date(ev1.startTime) : undefined;
          const d2 = ev2.startTime ? new Date(ev2.startTime) : undefined;
          if (d1 && d2) {
            return d2.getTime() - d1.getTime();
          } else {
            return -1;
          }
        });
        return events;
      }
    }
    queryAllEvents().then((events) => setEventState({ loading: false, events }));
  }, [selectedCalendar]);

  return eventState;
}

export function EventSelector({
  selectedEvent,
  setSelectedEvent,
  eventQuery,
  setEventQuery,
  selectedCalendar,
}: EventSelectorProps): React.ReactElement {
  const classes = useStyles();
  const eventState = useEvents(selectedCalendar);

  const handleSelectEvent = (_: React.ChangeEvent<unknown>, value: GoogleCalendarEvent | null): void => {
    setSelectedEvent(value);
  };

  const handleEventQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
    setEventQuery(value);
  };

  if (eventState.loading) {
    return <CircularProgress className={classes.progress} color="inherit" size={20} />;
  } else {
    return (
      <Autocomplete
        clearOnEscape
        noOptionsText="No such event"
        onChange={handleSelectEvent}
        value={selectedEvent}
        options={eventState.events}
        onInputChange={handleEventQueryChange}
        inputValue={eventQuery}
        getOptionLabel={(option): string => `${option.summary}`}
        renderOption={(option): React.ReactElement => (
          <ListItem component="div">
            <ListItemAvatar>
              <Avatar>{option.creator?.[0] || ''}</Avatar>
            </ListItemAvatar>
            <ListItemText primary={option.summary} secondary={new Date(option.startTime).toLocaleString()} />
          </ListItem>
        )}
        renderInput={(params: TextFieldProps): React.ReactElement => (
          <TextField {...params} required label="Selected Event" variant="outlined" className={classes.typeTextField} />
        )}
      />
    );
  }
}

type ContentProps = {
  selectedCalendar: GoogleCalendar | null;
  selectedEvent: GoogleCalendarEvent | null;
  setSelectedCalendar: (calendar: GoogleCalendar | null) => void;
  setSelectedEvent: (event: GoogleCalendarEvent | null) => void;
  setCalendarQuery: (value: string) => void;
  calendarQuery: string;
  setEventQuery: (value: string) => void;
  eventQuery: string;
};

export function ImportDialogContent({
  setSelectedEvent,
  setSelectedCalendar,
  selectedEvent,
  selectedCalendar,
  calendarQuery,
  setCalendarQuery,
  eventQuery,
  setEventQuery,
}: ContentProps): React.ReactElement {
  const classes = useStyles();

  const handleSelectCalendar = (_: React.ChangeEvent<unknown>, value: GoogleCalendar | null): void => {
    setSelectedCalendar(value);
  };

  const handleCalendarQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
    setCalendarQuery(value);
  };

  const { data: googleCalendarData, loading: googleCalendarsLoading } = useGetGoogleCalendarsQuery();

  const googleCalendars = googleCalendarData?.currentUser?.googleCalendars || [];

  return (
    <DialogContent dividers>
      {googleCalendarsLoading && (
        <div>
          <CircularProgress className={classes.progress} color="inherit" size={20} />
        </div>
      )}
      {!googleCalendarsLoading && (
        <Autocomplete
          clearOnEscape
          noOptionsText="No such calendar"
          onChange={handleSelectCalendar}
          value={selectedCalendar}
          options={googleCalendars}
          onInputChange={handleCalendarQueryChange}
          inputValue={calendarQuery}
          getOptionLabel={(option): string => `${option.summary}`}
          renderInput={(params: TextFieldProps): React.ReactElement => (
            <TextField
              {...params}
              required
              label="Selected Google Calendar"
              variant="outlined"
              className={classes.typeTextField}
            />
          )}
        />
      )}
      {selectedCalendar != null && (
        <EventSelector
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          eventQuery={eventQuery}
          setEventQuery={setEventQuery}
          selectedCalendar={selectedCalendar}
        />
      )}
    </DialogContent>
  );
}

const useStyles = makeStyles((theme) => ({
  typeTextField: { width: 500, marginTop: theme.spacing(2) },
  progress: { margin: theme.spacing(2) },
  actionColumn: { width: '12px' },
  title: { marginTop: theme.spacing(2), marginBottom: theme.spacing(2) },
  calendarTable: { marginBottom: theme.spacing(2) },
}));
